import '../modal/ui-modal-controller';
import { Labels } from '../../global/labels';
import { Digest } from '../../global/digest';
import { UIElement } from '../ui-element';
import {
    makePopoverHandler,
    appendModalControllerIfRequired,
} from '../../global/helpers';
import {
    addOutsideClickHandler,
    isParentOf,
    element,
} from '../../global/ui-helpers';
import { TabIndex } from '../../global/keyboard';
import { browser } from '../../global/index';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import {
    EventHandlerCallback,
    MaybeHTMLElement,
    NullOr,
} from '../../global/utils.type';
import { UIModal } from '../modal/ui-modal';
import { UIIconColors, UIIconGlyphRedesigns } from '../icon/ui-icon.helpers';
import type { UIIconColor, UIIconGlyphRedesign } from '../icon/ui-icon.types';
import styles from './ui-tooltip.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UITooltip
 * @element ui-tooltip
 * @classdesc Represents a class for <code>ui-tooltip</code> element.
 * Tooltip is a button with icon, which opens help dialog on click.
 * @property {boolean} [expanded] {@attr expanded} Tooltip is opened or not.
 * @property {string} [labelClose] {@attr label-close} Label for close button.
 * @property {string} [labelOpen] {@attr label-open} Label for open button.
 * @property {string} [labelLink] {@attr label-link} Label for link.
 * @property {("question" | "option" | "more" | "important" | "info")} [type="question"] {@attr type}
 * Type of the tooltip.
 *  {@desc question}
 *  {@desc option}
 *  {@desc more}
 *  {@desc important}
 *  {@desc info}
 * @property {("left" | "right")} [captionPosition] {@attr caption-position}
 * Position of the caption.
 *  {@desc left}
 *  {@desc right}
 * @property {HTMLButtonElement} popoverElement Shortcut to a popover element.
 * @property {HTMLDivElement} content Shortcut to a content element.
 * @property {HTMLButtonElement} control Shortcut to a control element.
 * @property {HTMLButtonElement} close Shortcut to close button.
 * @slot
 * @example
 * <ui-tooltip type="question" label-open="Open" label-close="Close">
 *   Any content you want inside the balloon comes here.
 * </ui-tooltip>
 */
@Component({ tag: 'ui-tooltip', styles })
export class UITooltip extends UIElement {
    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['expanded'];
    }

    /**
     * Define labels that could be localized.
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-tooltip', {
            open: 'Open tooltip',
            close: 'Close tooltip',
        });
    }

    @Attribute(Boolean) expanded?: boolean;
    @Attribute(String) labelClose: string = '';
    @Attribute(String) labelOpen: string = '';
    @Attribute(String) labelLink: string = '';
    @Attribute(String) type: string = 'question';
    @Attribute(String) captionPosition: string = 'right';

    @Child('.ui-tooltip__control') control!: HTMLButtonElement;
    @Child('.ui-tooltip__content') content!: HTMLDivElement;
    @Child('.ui-tooltip__close') close!: HTMLButtonElement;

    private _detachedPopover?: HTMLElement | null;
    private outsideClickRemover?: (() => void) | null;
    private clickOnControHandler?: () => void;
    private closePopoverHandler?: () => void;

    /**
     * Gets the icon glyph.
     * @type {string}
     * @readonly
     */
    get iconGlyph(): UIIconGlyphRedesign {
        switch (this.type) {
            case 'option':
            case 'more':
                return UIIconGlyphRedesigns.RedesignCircleEllipsisHorizontal;
            case 'important':
                return UIIconGlyphRedesigns.RedesignTooltipExclamation;
            case 'info':
                return UIIconGlyphRedesigns.RedesignTooltipInfo;
            default:
                return UIIconGlyphRedesigns.RedesignTooltipQuestion;
        }
    }

    get iconHoverGlyph(): UIIconGlyphRedesign {
        switch (this.type) {
            case 'more':
            case 'option':
                return UIIconGlyphRedesigns.RedesignCircleEllipsisHorizontalHover;
            case 'important':
                return UIIconGlyphRedesigns.RedesignTooltipExclamationHover;
            case 'info':
                return UIIconGlyphRedesigns.RedesignTooltipInfoHover;
            default:
                return UIIconGlyphRedesigns.RedesignTooltipQuestionHover;
        }
    }

    /**
     * Get ui-tooltip popover element.
     * @returns {HTMLElement | null}
     */
    get popoverElement(): MaybeHTMLElement {
        if (this._detachedPopover) {
            return this._detachedPopover;
        }
        return this.querySelector('.ui-tooltip__popover');
    }

    /**
     * Gets popover height.
     * @returns {number}
     */
    getPopoverHeight(): number {
        return this.popoverElement?.clientHeight || 0;
    }

    /**
     * Gets popover width.
     * @returns {number}
     */
    getPopoverWidth(): number {
        return this.popoverElement?.clientWidth || 0;
    }

    /**
     * Opens tooltip.
     * @fires event:modal-open
     */
    openDetachedPopover(): void {
        if (this._detachedPopover) {
            return;
        }
        const popoverHandler = makePopoverHandler(
            this.control.querySelector('ui-icon') || this.control,
            {
                popoverHeight:
                    /** @type {number} */ this.getPopoverHeight.bind(this),
                verticalOffset: 16,
                popoverWidth:
                    /** @type {number} */ this.getPopoverWidth.bind(this),
                horizontalOffset: -20,
                supportMiddleAlignment: false,
            }
        );
        this._detachedPopover = this.popoverElement;
        this.dispatchCustomEvent('modal-open', {
            type: 'popover',
            content: this.popoverElement,
            params: {
                classList: {
                    '-tooltip': true,
                    '-balloon': this.type === 'balloon',
                    '-basic': this.type === 'basic',
                    '-plain': this.type === 'plain',
                },
                attributes: {
                    'aria-label': this.resolveAccessibilityOpenLabel(),
                },
                position: () => {
                    if (browser.mobileMediaMatches()) {
                        return { align: 'fixed' };
                    }
                    return popoverHandler();
                },
                target: this,
                onOpen: () => {
                    const modal =
                        this.popoverElement?.closest<UIModal>('ui-modal');
                    modal?.focus();
                },
                onClose: () => {
                    this.control.focus();
                    this.expanded = false;
                },
            },
        });
    }

    /**
     * Closes tooltip.
     * @type {void}
     */
    closeDetachedPopover(): void {
        if (!this._detachedPopover) {
            return;
        }
        const modal = this._detachedPopover.closest<UIModal>('ui-modal');
        if (modal) {
            this._detachedPopover = null;
            modal.close();
        }
    }

    /**
     * Fires callback when the popover state has been changed.
     * @private
     */
    private onChangePopoverState(): void {
        if (!this.popoverElement) {
            return;
        }

        if (this.expanded) {
            this.openDetachedPopover();
            addOutsideClickHandler(
                this,
                this.handleClickOutside.bind(this) as EventHandlerCallback
            ).then((func) => (this.outsideClickRemover = func));
        } else {
            this.closeDetachedPopover();
            this.outsideClickRemover?.();
        }
    }

    /**
     * Sets tooltip popover as hidden and moves focus to control.
     * @type {void}
     */
    closePopover(): void {
        this.expanded = false;
        this.control.focus();
    }

    /**
     * Toggles popover status - show / hide.
     * @returns {boolean} - true if popover is open after firing, otherwise false.
     * @private
     */
    togglePopover(): boolean {
        this.expanded = !this.expanded;
        return this.expanded;
    }

    /**
     * Fires callback when user clicked outside a tooltip element.
     * @param {Event} event
     * @private
     */
    private handleClickOutside(event: Event): void {
        if (
            this.expanded &&
            event?.target instanceof Element &&
            !isParentOf(this, event.target) &&
            this.popoverElement &&
            !isParentOf(this.popoverElement, event.target)
        ) {
            this.closePopover();
        }
    }

    /**
     * Fires callback when a user clicks on the tooltip control.
     * @private
     */
    private handleClickOnControl(): void {
        this.togglePopover();
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'expanded':
                this.onChangePopoverState();
                break;
        }
    }

    /**
     * Resolves opening label for screen-readers.
     * @private
     * @returns {string}
     */
    private resolveAccessibilityOpenLabel(): TranslationLabels.Label {
        return this.labelOpen || this.labelLink || UITooltip.labels.open;
    }

    /**
     * Resolves closing label for screen-readers.
     * @returns {string}
     * @private
     */
    private resolveAccessibilityCloseLabel(): TranslationLabels.Label {
        return this.labelClose || UITooltip.labels.close;
    }

    /**
     * Tries to generate a unique but guessable content ID for use with aria-describedby outside of <ui-tooltip>.
     * Ensures the content can be referenced for accessibility by <ui-field> or other elements.
     * @returns {string}
     */
    getContentId(): string {
        return this.id ? `${this.id}-content` : Digest.randomId();
    }

    getIconColor(): UIIconColor {
        switch (this.type) {
            case 'option':
            case 'more':
                return UIIconColors.TEAL;
            default:
                return UIIconColors.BARK_70;
        }
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        if (this.clickOnControHandler) {
            this.control?.addEventListener('click', this.clickOnControHandler);
        }
        if (this.closePopoverHandler) {
            this.close?.addEventListener('click', this.closePopoverHandler);
        }
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        if (this.clickOnControHandler) {
            this.control?.removeEventListener(
                'click',
                this.clickOnControHandler
            );
        }
        if (this.closePopoverHandler) {
            this.close?.removeEventListener('click', this.closePopoverHandler);
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const popoverId = Digest.randomId();
        const contentId = this.getContentId();
        const labelElement = this.labelLink
            ? element`<span>${this.labelLink}</span>`
            : '';
        const isLinkType = this.type === 'option' || this.type === 'more';

        this.append(
            element`
              <button
                class="ui-tooltip__control${isLinkType ? ' -iconed' : ''}"
                type="button"
                aria-haspopup="dialog"
                aria-controls="${popoverId}"
                aria-label="${this.resolveAccessibilityOpenLabel()}"
              >
                ${this.captionPosition === 'left' && labelElement}
                <ui-icon
                  class="-has-hover-glyph"
                  glyph="${this.iconGlyph}"
                  hover-glyph="${this.iconHoverGlyph}"
                  color="${this.getIconColor()}" 
                ></ui-icon>
                ${this.captionPosition !== 'left' && labelElement}
              </button>`,
            element`
            <div
              id="${popoverId}"
              class="ui-tooltip__popover"
              tabindex="${TabIndex.Inactive}"
            >
              <div
                id="${contentId}"
                class="ui-tooltip__content"
              >${this.detachChildNodes()}</div>
              <button
                class="ui-tooltip__close -visually-hidden -iconed"
                type="button"
                title="${this.resolveAccessibilityCloseLabel()}"
              >
                <ui-icon
                  glyph="cross"
                  color="${UIIconColors.DEFAULT}"
                ></ui-icon>
              </button>
              <div class="ui-tooltip__pointer-wrapper">
                <div class="ui-tooltip__pointer"></div>
              </div>
            </div>
          `
        );
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        appendModalControllerIfRequired();
        this.clickOnControHandler = this.handleClickOnControl.bind(this);
        this.closePopoverHandler = this.closePopover.bind(this);
        this.subscribeEvents();
        this.onChangePopoverState();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.closeDetachedPopover();
        this.unsubscribeEvents();
        this.outsideClickRemover?.();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
